import { Pause, Play, RotateCcw, StepForward } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { advanceDemo, DEMO_JOBS, DEMO_STAGES, DEMO_UPDATES, nextDemoTarget, type DemoJob } from './landing-data';

const TICK_MS = 2200;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * A pretend dispatch board, for the front page.
 *
 * Sample jobs only: no API call, no stored state, nothing sent. It plays by
 * itself while it is on screen (unless the visitor asked for less motion, in
 * which case it waits to be clicked), and any card can be clicked to move it
 * on by hand. Beside the board is what that job's customer has been sent.
 */
export function LandingDemo() {
  const [jobs, setJobs] = useState<DemoJob[]>(() => [...DEMO_JOBS]);
  const [selected, setSelected] = useState<string>(DEMO_JOBS[0]!.ref);
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());
  const [visible, setVisible] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const root = useRef<HTMLDivElement>(null);

  // Only run while somebody can see it.
  useEffect(() => {
    const node = root.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setVisible(Boolean(entry?.isIntersecting)), {
      threshold: 0.25,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  function step(current: DemoJob[], chosen: string | null) {
    const target = nextDemoTarget(current, chosen);
    if (!target) {
      setJobs([...DEMO_JOBS]);
      setSelected(DEMO_JOBS[0]!.ref);
      setAnnouncement('Demo board reset.');
      return;
    }
    const next = advanceDemo(current, target);
    const moved = next.find((job) => job.ref === target)!;
    setJobs(next);
    setSelected(target);
    setAnnouncement(`${target} moved to ${DEMO_STAGES[moved.stage]!.label}.`);
  }

  // Each tick reads the latest board; the callback is rebuilt when it changes.
  const tick = useRef(() => {});
  tick.current = () => step(jobs, selected);

  useEffect(() => {
    if (!playing || !visible) return;
    const id = window.setInterval(() => tick.current(), TICK_MS);
    return () => window.clearInterval(id);
  }, [playing, visible]);

  function reset() {
    setJobs([...DEMO_JOBS]);
    setSelected(DEMO_JOBS[0]!.ref);
    setAnnouncement('Demo board reset.');
  }

  const current = jobs.find((job) => job.ref === selected) ?? jobs[0]!;
  const sent = DEMO_UPDATES.slice(0, current.stage + 1).map((text) => text.replace('{ref}', current.ref));

  return (
    <div className="demo" ref={root}>
      <div className="demo-bar">
        <span className="demo-tag">Demo · sample jobs</span>
        <button
          type="button"
          className="btn ghost small"
          onClick={() => setPlaying((value) => !value)}
          aria-pressed={playing}
        >
          {playing ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
          {playing ? 'Pause' : 'Play'}
        </button>
        <button type="button" className="btn ghost small" onClick={() => step(jobs, selected)}>
          <StepForward size={14} aria-hidden="true" /> Next step
        </button>
        <button type="button" className="btn ghost small" onClick={reset}>
          <RotateCcw size={14} aria-hidden="true" /> Reset
        </button>
      </div>

      <div className="demo-body">
        <div className="board" role="group" aria-label="Demo dispatch board">
          {DEMO_STAGES.map((stage, index) => {
            const here = jobs.filter((job) => job.stage === index);
            return (
              <div className="col" key={stage.id}>
                <h3>
                  <span>{stage.label}</span>
                  <span aria-label={`${here.length} jobs`}>{here.length}</span>
                </h3>
                <ul>
                  {here.map((job) => (
                    <li key={`${job.ref}-${job.stage}`}>
                      <button
                        type="button"
                        className="job"
                        aria-pressed={job.ref === selected}
                        onClick={() => {
                          setPlaying(false);
                          // A resolved job has nowhere to go: show its story instead.
                          if (job.stage < DEMO_STAGES.length - 1) step(jobs, job.ref);
                          else setSelected(job.ref);
                        }}
                        aria-label={
                          job.stage < DEMO_STAGES.length - 1
                            ? `${job.ref}, ${job.title}, ${stage.label}. Move to the next stage.`
                            : `${job.ref}, ${job.title}, ${stage.label}. Show what the customer was sent.`
                        }
                      >
                        <span className="ref">{job.ref}</span>
                        <span className="title">{job.title}</span>
                        <span className="meta">{job.area}</span>
                        <span className="meta">{job.tech ?? 'Unassigned'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="phone">
          <h3>{current.ref} · what happens</h3>
          <div className="meter" aria-hidden="true">
            {DEMO_STAGES.map((stage, index) => (
              <span key={stage.id} className={index <= current.stage ? 'on' : undefined} />
            ))}
          </div>
          <p className="stage-note">{DEMO_STAGES[current.stage]!.note}</p>
          <h3>Customer was sent</h3>
          <ul className="sms">
            {sent.map((text, index) => (
              <li key={index}>
                <small>SMS · {DEMO_STAGES[index]!.label}</small>
                {text}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="demo-foot">
        A demonstration with made-up jobs. Click a card to move it on. Nothing here is saved or sent.
      </p>
      <p className="sr" aria-live="polite">
        {announcement}
      </p>
    </div>
  );
}
